import { cn } from '@/utils/cn';
import { stageLabel } from './recruitmentUtils';

const STEPS = [
  { key: 'screening', label: 'Screening', stages: ['APPLIED', 'SCREENING'] },
  { key: 'assessment', label: 'Assessment', stages: ['SCREENING'] },
  { key: 'interview', label: 'Interview', stages: ['INTERVIEW_SCHEDULED', 'INTERVIEWED'] },
  { key: 'selection', label: 'Selection', stages: ['SELECTED'] },
  { key: 'offer', label: 'Offer', stages: ['OFFER_SENT'] },
  { key: 'acceptance', label: 'Acceptance', stages: ['OFFER_ACCEPTED'] },
  { key: 'preboarding', label: 'Preboarding', stages: ['PREBOARDING'] },
  { key: 'onboarding', label: 'Onboarding', stages: ['ONBOARDING'] },
  { key: 'hire', label: 'Employee', stages: ['HIRED'] },
] as const;

type ApprovalStatus = {
  selectionStatus?: string;
  offerStatus?: string;
  offerLetterStatus?: string;
};

type Props = {
  stage: string;
  approvalStatus?: ApprovalStatus;
};

function stepIndex(stage: string, approval?: ApprovalStatus): number {
  const s = stage.toUpperCase();
  if (s === 'HIRED') return 8;
  if (s === 'ONBOARDING') return 7;
  if (s === 'PREBOARDING') return 6;
  if (s === 'OFFER_ACCEPTED') return 5;
  if (s === 'OFFER_SENT') {
    if (approval?.offerStatus === 'PENDING') return 4;
    if (approval?.offerLetterStatus === 'SENT') return 5;
    return 4;
  }
  if (s === 'SELECTED') {
    if (approval?.selectionStatus === 'PENDING') return 3;
    return 4;
  }
  if (s === 'INTERVIEWED') return 3;
  if (s === 'INTERVIEW_SCHEDULED') return 2;
  if (s === 'SCREENING') return 1;
  if (s === 'APPLIED') return 0;
  if (s === 'REJECTED' || s === 'OFFER_DECLINED') return -1;
  return 0;
}

export function nextActionHint(stage: string, approval?: ApprovalStatus): string {
  const s = stage.toUpperCase();
  if (s === 'REJECTED' || s === 'OFFER_DECLINED') return 'This candidate is no longer in the active hiring flow.';
  if (s === 'APPLIED' || s === 'SCREENING') return 'Run auto screening, schedule assessment, then schedule an interview.';
  if (s === 'INTERVIEW_SCHEDULED') return 'Complete the interview to move the candidate to selected.';
  if (s === 'INTERVIEWED') return 'Candidate should advance to selected after interview completion.';
  if (s === 'SELECTED') {
    if (approval?.selectionStatus === 'PENDING') return 'Approve selection, then create the offer letter.';
    return 'Create and send the offer letter.';
  }
  if (s === 'OFFER_SENT') {
    if (approval?.offerStatus === 'PENDING') return 'Approve the offer, then accept on behalf of the candidate or share the portal link.';
    return 'Accept the offer to start preboarding and background verification.';
  }
  if (s === 'PREBOARDING') return 'Complete preboarding checklist and BGV, then onboarding starts automatically.';
  if (s === 'OFFER_ACCEPTED') return 'Preboarding is starting — complete documents in the candidate portal.';
  if (s === 'ONBOARDING') return 'Complete all onboarding tasks, approve onboarding, then create the employee record.';
  if (s === 'HIRED') return 'Employee created — complete probation review when due.';
  return 'Continue the hiring workflow.';
}

export function HrHiringStepGuide({ stage, approvalStatus }: Props) {
  const current = stepIndex(stage, approvalStatus);
  const terminal = stage === 'REJECTED' || stage === 'OFFER_DECLINED';

  return (
    <div className="card p-4">
      <h4 className="mb-1 text-sm font-semibold text-slate-900 dark:text-slate-100">Hiring progress</h4>
      <p className="mb-3 text-xs text-muted">{nextActionHint(stage, approvalStatus)}</p>
      {terminal ? (
        <p className="text-sm text-red-600 dark:text-red-400">Terminal stage: {stageLabel(stage)}</p>
      ) : (
        <ol className="flex flex-wrap gap-2">
          {STEPS.map((step, idx) => (
            <li
              key={step.key}
              className={cn(
                'rounded-full px-2.5 py-1 text-xs font-medium',
                idx < current && 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
                idx === current && 'bg-primary-100 text-primary-800 ring-2 ring-primary-400 dark:bg-primary-950 dark:text-primary-200',
                idx > current && 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
              )}
            >
              {step.label}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
